import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useGetUserByTokenMutation } from '@/hooks/eventHooks/authHooks/useGetUserByTokenMutation';
import { Tokens, UserResponse } from '@/services/api/auth/auth.service';
import { authHelper } from '@/services/AuthHelpers';

type AuthContextProps = {
  auth: string | undefined;
  saveAuth: (auth: Tokens | undefined) => void;
  currentUser: UserResponse | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserResponse | undefined>>;
  logout: () => void;
};

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [auth, setAuth] = useState<string | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserResponse | undefined>();

  const saveAuth = (tokens: Tokens | undefined) => {
    if (tokens) {
      setAuth(tokens?.access.token);
      authHelper.setAuth(tokens?.access.token);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = () => {
    saveAuth(undefined);
    setCurrentUser(undefined);
  };

  return (
    <AuthContext.Provider value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit = ({ children }: { children: React.ReactNode }) => {
  const { auth, logout } = useAuth();

  const didRequest = useRef(false);
  const { mutate } = useGetUserByTokenMutation();

  useEffect(() => {
    if (auth) {
      if (!didRequest.current) {
        mutate();
        didRequest.current = true;
      }
    } else {
      logout();
    }
  }, [auth, mutate]);

  return <>{children}</>;
};

export { AuthInit, AuthProvider, useAuth };
